'use client';

import React, { useState, useEffect } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import BookingCalendar from './BookingCalendar';
import RoomSelector from './RoomSelector';
import GuestSelector from './GuestSelector';
import BookingSummary from './BookingSummary';
import GuestInformation from './GuestInformation';
import PaymentSection from './PaymentSection';
import Icon from '@/components/ui/AppIcon';
import { bookingService } from '@/services/bookingService';
import { roomService } from '@/services/roomService';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

interface Room {
  id: string;
  name: string;
  description: string;
  price: number;
  maxGuests: number;
  amenities: string[];
  images: Array<{
    url: string;
    alt: string;
  }>;
  available: boolean;
}

interface GuestInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  specialRequests: string;
  arrivalTime: string;
  breakfastIncluded: boolean;
  cleaningService: boolean;
  earlyCheckIn: boolean;
  lateCheckOut: boolean;
}

interface PaymentInfo {
  method: 'card' | 'paypal' | '';
  cardholderName: string;
  billingAddress: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  saveCard: boolean;
}

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '');

const BookingForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [currentStep, setCurrentStep] = useState(1);
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedDates, setSelectedDates] = useState<{
    checkIn: Date | null;
    checkOut: Date | null;
  }>({
    checkIn: null,
    checkOut: null
  });
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    specialRequests: '',
    arrivalTime: '',
    breakfastIncluded: false,
    cleaningService: false,
    earlyCheckIn: false,
    lateCheckOut: false
  });
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
    method: '',
    cardholderName: '',
    billingAddress: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    saveCard: false
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);

  const [rooms, setRooms] = useState<Room[]>([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState(true);
  const [unavailableDates, setUnavailableDates] = useState<Date[]>([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    loadRooms();
  }, []);

  // Carica disponibilità quando viene selezionata una camera
  useEffect(() => {
    if (selectedRooms.length > 0) {
      loadUnavailableDates(selectedRooms[0]);
    } else {
      setUnavailableDates([]);
    }
  }, [selectedRooms]);

  // 🆕 Gestione ritorno da Stripe Checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');

    if (success === 'true' && sessionId) {
      const pendingBookingData = sessionStorage.getItem('pendingBooking');
      
      if (pendingBookingData) {
        const bookingData = JSON.parse(pendingBookingData);
        
        // Salva prenotazione nel database
        bookingService.create(
          {
            guestId: null,
            roomId: bookingData.roomId,
            checkInDate: bookingData.checkInDate,
            checkOutDate: bookingData.checkOutDate,
            numberOfGuests: bookingData.numberOfGuests,
            totalPrice: bookingData.totalPrice.toString(),
            bookingStatus: 'confirmed',
            paymentStatus: 'paid',
            guestName: `${bookingData.guestInfo.firstName} ${bookingData.guestInfo.lastName}`,
            guestEmail: bookingData.guestInfo.email,
            guestPhone: bookingData.guestInfo.phone,
            specialRequests: bookingData.guestInfo.specialRequests
          },
          bookingData.roomName
        ).then(({ data, error }) => {
          if (!error && data) {
            // Invia email di conferma
            fetch(
              `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-booking-email`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`
                },
                body: JSON.stringify({
                  bookingId: data.id,
                  guestEmail: bookingData.guestInfo.email,
                  guestName: `${bookingData.guestInfo.firstName} ${bookingData.guestInfo.lastName}`,
                  guestPhone: bookingData.guestInfo.phone,
                  roomName: bookingData.roomName,
                  checkInDate: bookingData.checkInDate,
                  checkOutDate: bookingData.checkOutDate,
                  numberOfGuests: bookingData.numberOfGuests,
                  totalPrice: bookingData.totalPrice,
                  ownerEmail: 'prenot.23paglia.bergamoitaly@gmail.com'
                })
              }
            );

            alert(`✅ Pagamento completato con successo!\n\nPrenotazione confermata!\nID: ${data.id}\n\nRiceverai una email di conferma a ${bookingData.guestInfo.email}`);
            
            // Pulisci sessionStorage
            sessionStorage.removeItem('pendingBooking');
            
            // Pulisci URL
            window.history.replaceState({}, '', '/booking-system');
          }
        });
      }
    }
  }, []);

  const loadRooms = async () => {
    setIsLoadingRooms(true);
    try {
      const { data, error } = await roomService.getAll();
      if (error) throw error;
      
      if (data) {
        const formattedRooms = data.map(room => ({
          id: room.id,
          name: room.name,
          description: room.description,
          price: Number(room.pricePerNight),
          maxGuests: room.maxGuests,
          amenities: room.amenities || [],
          images: room.images?.map(img => ({
            url: img.url,
            alt: img.alt || `${room.name} room image`
          })) || [],
          available: true
        }));
        setRooms(formattedRooms);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      setIsLoadingRooms(false);
    }
  };

  const loadUnavailableDates = async (roomId: string) => {
    setIsLoadingAvailability(true);
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);

      const { data: bookings } = await supabase
        .from('bookings')
        .select('room_id, check_in_date, check_out_date')
        .eq('room_id', roomId)
        .gte('check_out_date', startDate.toISOString().split('T')[0])
        .lte('check_in_date', endDate.toISOString().split('T')[0])
        .in('status', ['confirmed', 'pending']);

      const { data: blockedDates } = await supabase
        .from('blocked_dates')
        .select('room_id, date')
        .eq('room_id', roomId)
        .gte('date', startDate.toISOString().split('T')[0])
        .lte('date', endDate.toISOString().split('T')[0]);

      const unavailable: Date[] = [];

      if (bookings) {
        bookings.forEach(booking => {
          const checkIn = new Date(booking.check_in_date);
          const checkOut = new Date(booking.check_out_date);
          
          for (let d = new Date(checkIn); d < checkOut; d.setDate(d.getDate() + 1)) {
            unavailable.push(new Date(d));
          }
        });
      }

      if (blockedDates) {
        blockedDates.forEach(blocked => {
          unavailable.push(new Date(blocked.date));
        });
      }

      setUnavailableDates(unavailable);
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:274:6" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="274" data-component-end-line="287" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:275:8" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="275" data-component-end-line="286" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:276:10" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="276" data-component-end-line="285" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
            <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:277:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="277" data-component-end-line="277" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20mb-8%22%7D" className="h-8 bg-muted rounded mb-8"></div>
            <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:278:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="278" data-component-end-line="284" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:279:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="279" data-component-end-line="282" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-8%22%7D" className="lg:col-span-2 space-y-8">
                <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:280:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="280" data-component-end-line="280" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" className="h-96 bg-muted rounded-lg"></div>
                <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:281:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="281" data-component-end-line="281" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" className="h-64 bg-muted rounded-lg"></div>
              </div>
              <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:283:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="283" data-component-end-line="283" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" className="h-96 bg-muted rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleRoomSelect = (roomId: string) => {
    if (selectedRooms.includes(roomId)) {
      setSelectedRooms([]);
      setSelectedDates({ checkIn: null, checkOut: null });
    } else {
      setSelectedRooms([roomId]);
      setSelectedDates({ checkIn: null, checkOut: null });
    }
  };

  const calculateTotal = () => {
    if (!selectedDates.checkIn || !selectedDates.checkOut) return 0;
    const nights = Math.ceil((selectedDates.checkOut.getTime() - selectedDates.checkIn.getTime()) / (1000 * 3600 * 24));
    const subtotal = selectedRooms.reduce((total, roomId) => {
      const room = rooms.find((r) => r.id === roomId);
      return total + (room ? room.price * nights : 0);
    }, 0);
    
    let extras = 0;
    if (guestInfo.breakfastIncluded) extras += 10 * nights;
    if (guestInfo.cleaningService) extras += 10 * nights;
    
    return (subtotal + extras) * 1.07;
  };

  const canProceedToStep = (step: number) => {
  switch (step) {
    case 1:
      return selectedDates.checkIn && selectedDates.checkOut && selectedRooms.length > 0;
    case 2:
      return adults > 0;
    case 3:
      return guestInfo.firstName && guestInfo.lastName && guestInfo.email && guestInfo.phone;
    case 4:
      return paymentInfo.method === 'card';  // 🆕 SEMPLIFICATO!
    default:
      return true;
  }
};

  const handlePayPalApprove = async (orderId: string) => {
    if (!selectedRooms.length || !selectedDates.checkIn || !selectedDates.checkOut) {
      alert('Seleziona date e camere per continuare');
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);
    setPaypalOrderId(orderId);

    try {
      const selectedRoom = rooms.find(r => r.id === selectedRooms[0]);
      if (!selectedRoom) {
        throw new Error('Camera non trovata');
      }

      const totalAmount = calculateTotal();

      const { data: bookingData, error: bookingError } = await bookingService.create(
        {
          guestId: null,
          roomId: selectedRoom.id,
          checkInDate: selectedDates.checkIn.toISOString().split('T')[0],
          checkOutDate: selectedDates.checkOut.toISOString().split('T')[0],
          numberOfGuests: adults + children,
          totalPrice: totalAmount.toString(),
          bookingStatus: 'confirmed',
          paymentStatus: 'paid',
          guestName: `${guestInfo.firstName} ${guestInfo.lastName}`,
          guestEmail: guestInfo.email,
          guestPhone: guestInfo.phone,
          specialRequests: guestInfo.specialRequests
        },
        selectedRoom.name
      );

      if (bookingError) {
        throw bookingError;
      }

      try {
        const emailResponse = await fetch(
          `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-booking-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              bookingId: bookingData?.id,
              guestEmail: guestInfo.email,
              guestName: `${guestInfo.firstName} ${guestInfo.lastName}`,
              guestPhone: guestInfo.phone,
              roomName: selectedRoom.name,
              checkInDate: selectedDates.checkIn.toISOString().split('T')[0],
              checkOutDate: selectedDates.checkOut.toISOString().split('T')[0],
              numberOfGuests: adults + children,
              totalPrice: totalAmount,
              ownerEmail: 'prenot.23paglia.bergamoitaly@gmail.com'
            })
          }
        );

        if (!emailResponse.ok) {
          console.error('Errore invio email:', await emailResponse.text());
        } else {
          console.log('✅ Email inviate con successo');
        }
      } catch (emailError) {
        console.error('Errore chiamata funzione email:', emailError);
      }

      alert(`✅ Prenotazione confermata!\n\nID: ${bookingData?.id}\n\nRiceverai una email di conferma a ${guestInfo.email} con tutti i dettagli del tuo soggiorno.\n\nControlla anche la cartella spam se non vedi l'email entro pochi minuti.`);
      
      setSelectedRooms([]);
      setSelectedDates({ checkIn: null, checkOut: null });
      setGuestInfo({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        specialRequests: '',
        arrivalTime: '',
        breakfastIncluded: false,
        cleaningService: false,
        earlyCheckIn: false,
        lateCheckOut: false
      });
      setPaymentInfo({
        method: '',
        cardholderName: '',
        billingAddress: {
          street: '',
          city: '',
          postalCode: '',
          country: ''
        },
        saveCard: false
      });
      setCurrentStep(1);
      
      await loadUnavailableDates(selectedRoom.id);
      
    } catch (error) {
      console.error('Booking error:', error);
      setPaymentError(
        error instanceof Error ? error.message : 'Errore nel processare la prenotazione'
      );
      alert(`❌ Errore: ${error instanceof Error ? error.message : 'Errore nel processare la prenotazione'}\n\nRiprova o contattaci per assistenza.`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 🆕 FUNZIONE MODIFICATA - Usa Stripe Checkout
  const handleBookingSubmit = async () => {
    if (!selectedRooms.length || !selectedDates.checkIn || !selectedDates.checkOut) {
      alert('Seleziona date e camere per continuare');
      return;
    }

    if (paymentInfo.method === 'paypal') {
      return;
    }

    if (!stripe || !elements) {
      alert('Stripe non è ancora pronto. Riprova tra un attimo.');
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      const selectedRoom = rooms.find(r => r.id === selectedRooms[0]);
      if (!selectedRoom) {
        throw new Error('Camera non trovata');
      }

      const totalAmount = calculateTotal();

      // Crea Checkout Session
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: totalAmount,
          roomName: selectedRoom.name,
          checkInDate: selectedDates.checkIn.toISOString().split('T')[0],
          checkOutDate: selectedDates.checkOut.toISOString().split('T')[0],
          guestEmail: guestInfo.email,
          guestName: `${guestInfo.firstName} ${guestInfo.lastName}`,
        }),
      });

      if (!response.ok) {
        throw new Error('Errore nella creazione della sessione di pagamento');
      }

     const { sessionId, url } = await response.json();

// Salva dati temporanei in sessionStorage per dopo il pagamento
sessionStorage.setItem('pendingBooking', JSON.stringify({
  roomId: selectedRoom.id,
  roomName: selectedRoom.name,
  checkInDate: selectedDates.checkIn.toISOString().split('T')[0],
  checkOutDate: selectedDates.checkOut.toISOString().split('T')[0],
  numberOfGuests: adults + children,
  totalPrice: totalAmount,
  guestInfo: guestInfo,
}));

// 🆕 REDIRECT DIRETTO ALL'URL STRIPE
if (url) {
  window.location.href = url;
} else {
  throw new Error('URL sessione non ricevuto');
}
    } catch (error) {
      console.error('Booking error:', error);
      setPaymentError(
        error instanceof Error ? error.message : 'Errore nel processare la prenotazione'
      );
      alert(`❌ Errore: ${error instanceof Error ? error.message : 'Errore nel processare la prenotazione'}\n\nRiprova o contattaci per assistenza.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const steps = [
    { number: 1, title: 'Camera & Date', description: 'Seleziona camera e date' },
    { number: 2, title: 'Ospiti', description: 'Numero di ospiti' },
    { number: 3, title: 'Informazioni', description: 'Dati personali' },
    { number: 4, title: 'Pagamento', description: 'Metodo di pagamento' }
  ];

  return (
    <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:532:4" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="532" data-component-end-line="719" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:533:6" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="533" data-component-end-line="564" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
        <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:534:8" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="534" data-component-end-line="563" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:536:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="536" data-component-end-line="561" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%22%7D" key={step.number} className="flex items-center">
              <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:537:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="537" data-component-end-line="547" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                currentStep >= step.number ?
                  'bg-primary border-primary text-primary-foreground' :
                  'border-border text-text-secondary'
              }`}>
                {currentStep > step.number ? (
                  <Icon data-component-id="src/app/booking-system/components/BookingInteractive.tsx:543:18" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="543" data-component-end-line="543" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={20} />
                ) : (
                  <span data-component-id="src/app/booking-system/components/BookingInteractive.tsx:545:18" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="545" data-component-end-line="545" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%22%7D" className="font-semibold">{step.number}</span>
                )}
              </div>
              <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:548:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="548" data-component-end-line="555" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22ml-3%20hidden%20sm%3Ablock%22%7D" className="ml-3 hidden sm:block">
                <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:549:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="549" data-component-end-line="553" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`text-sm font-semibold ${
                  currentStep >= step.number ? 'text-primary' : 'text-text-secondary'
                }`}>
                  {step.title}
                </div>
                <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:554:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="554" data-component-end-line="554" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-text-secondary%22%7D" className="text-xs text-text-secondary">{step.description}</div>
              </div>
              {index < steps.length - 1 && (
                <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:557:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="557" data-component-end-line="559" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`hidden sm:block w-16 h-0.5 ml-6 ${
                  currentStep > step.number ? 'bg-primary' : 'bg-border'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:566:6" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="566" data-component-end-line="718" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:567:8" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="567" data-component-end-line="704" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-8%22%7D" className="lg:col-span-2 space-y-8">
          {currentStep === 1 && (
            <>
              {isLoadingRooms ? (
                <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:571:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="571" data-component-end-line="574" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-8%22%7D" className="text-center py-8">
                  <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:572:18" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="572" data-component-end-line="572" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-12%20w-12%20border-b-2%20border-primary%20mx-auto%22%7D" className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p data-component-id="src/app/booking-system/components/BookingInteractive.tsx:573:18" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="573" data-component-end-line="573" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20mt-4%22%2C%22textContent%22%3A%22Caricamento%20camere...%22%7D" className="text-text-secondary mt-4">Caricamento camere...</p>
                </div>
              ) : (
                <RoomSelector data-component-id="src/app/booking-system/components/BookingInteractive.tsx:576:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="576" data-component-end-line="581" data-component-file="BookingInteractive.tsx" data-component-name="RoomSelector" data-component-content="%7B%22elementName%22%3A%22RoomSelector%22%7D"
                  rooms={rooms}
                  selectedRooms={selectedRooms}
                  onRoomSelect={handleRoomSelect}
                  guests={adults + children}
                />
              )}

              {selectedRooms.length > 0 && (
                <>
                  {isLoadingAvailability ? (
                    <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:587:20" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="587" data-component-end-line="599" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20p-6%20bergamo-shadow%22%7D" className="bg-card rounded-lg p-6 bergamo-shadow">
                      <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:588:22" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="588" data-component-end-line="595" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
                        <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:589:24" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="589" data-component-end-line="589" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20mb-4%22%7D" className="h-6 bg-muted rounded mb-4"></div>
                        <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:590:24" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="590" data-component-end-line="594" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-7%20gap-2%22%7D" className="grid grid-cols-7 gap-2">
                          {Array.from({ length: 35 }).map((_, i) => (
                            <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:592:28" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="592" data-component-end-line="592" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-10%20bg-muted%20rounded%22%7D" key={i} className="h-10 bg-muted rounded"></div>
                          ))}
                        </div>
                      </div>
                      <p data-component-id="src/app/booking-system/components/BookingInteractive.tsx:596:22" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="596" data-component-end-line="598" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-center%20text-text-secondary%20mt-4%22%2C%22textContent%22%3A%22Caricamento%20disponibilit%C3%A0%20per%20...%22%7D" className="text-center text-text-secondary mt-4">
                        Caricamento disponibilità per {rooms.find(r => r.id === selectedRooms[0])?.name}...
                      </p>
                    </div>
                  ) : (
                    <>
                      <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:602:22" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="602" data-component-end-line="609" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-primary%2F5%20border%20border-primary%2F20%20rounded-lg%20p-4%20mb-4%22%7D" className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
                        <p data-component-id="src/app/booking-system/components/BookingInteractive.tsx:603:24" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="603" data-component-end-line="605" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-primary%20font-medium%22%2C%22textContent%22%3A%22%F0%9F%93%85%20Calendario%20per%3A%22%7D" className="text-primary font-medium">
                          📅 Calendario per: <strong data-component-id="src/app/booking-system/components/BookingInteractive.tsx:604:45" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="604" data-component-end-line="604" data-component-file="BookingInteractive.tsx" data-component-name="strong" data-component-content="%7B%22elementName%22%3A%22strong%22%7D">{rooms.find(r => r.id === selectedRooms[0])?.name}</strong>
                        </p>
                        <p data-component-id="src/app/booking-system/components/BookingInteractive.tsx:606:24" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="606" data-component-end-line="608" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%20mt-1%22%2C%22textContent%22%3A%22Le%20date%20mostrate%20sono%20filtrate%20per%20questa%20camera%20specifica%22%7D" className="text-sm text-text-secondary mt-1">
                          Le date mostrate sono filtrate per questa camera specifica
                        </p>
                      </div>
                      <BookingCalendar data-component-id="src/app/booking-system/components/BookingInteractive.tsx:610:22" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="610" data-component-end-line="614" data-component-file="BookingInteractive.tsx" data-component-name="BookingCalendar" data-component-content="%7B%22elementName%22%3A%22BookingCalendar%22%7D"
                        selectedDates={selectedDates}
                        onDateSelect={setSelectedDates}
                        unavailableDates={unavailableDates}
                      />
                    </>
                  )}
                </>
              )}

              {selectedRooms.length === 0 && !isLoadingRooms && (
                <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:621:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="621" data-component-end-line="625" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-yellow-50%20border%20border-yellow-200%20rounded-lg%20p-6%20text-center%22%7D" className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <p data-component-id="src/app/booking-system/components/BookingInteractive.tsx:622:18" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="622" data-component-end-line="624" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-yellow-800%20font-medium%22%2C%22textContent%22%3A%22%F0%9F%91%86%20Seleziona%20prima%20una%20camera%20per%20vedere%20il%20calendario%20delle%20disponibilit%C3%A0%22%7D" className="text-yellow-800 font-medium">
                    👆 Seleziona prima una camera per vedere il calendario delle disponibilità
                  </p>
                </div>
              )}
            </>
          )}

          {currentStep === 2 && (
            <GuestSelector data-component-id="src/app/booking-system/components/BookingInteractive.tsx:631:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="631" data-component-end-line="636" data-component-file="BookingInteractive.tsx" data-component-name="GuestSelector" data-component-content="%7B%22elementName%22%3A%22GuestSelector%22%7D"
              adults={adults}
              children={children}
              onAdultsChange={setAdults}
              onChildrenChange={setChildren}
            />
          )}

          {currentStep === 3 && (
            <GuestInformation data-component-id="src/app/booking-system/components/BookingInteractive.tsx:640:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="640" data-component-end-line="643" data-component-file="BookingInteractive.tsx" data-component-name="GuestInformation" data-component-content="%7B%22elementName%22%3A%22GuestInformation%22%7D"
              guestInfo={guestInfo}
              onGuestInfoChange={setGuestInfo}
            />
          )}

          {currentStep === 4 && (
            <PaymentSection data-component-id="src/app/booking-system/components/BookingInteractive.tsx:647:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="647" data-component-end-line="652" data-component-file="BookingInteractive.tsx" data-component-name="PaymentSection" data-component-content="%7B%22elementName%22%3A%22PaymentSection%22%7D"
              paymentInfo={paymentInfo}
              onPaymentInfoChange={setPaymentInfo}
              total={calculateTotal()}
              onPayPalApprove={handlePayPalApprove}
            />
          )}

          {paymentError && (
            <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:656:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="656" data-component-end-line="662" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-error%2F10%20border%20border-error%20text-error%20p-4%20rounded-lg%22%7D" className="bg-error/10 border border-error text-error p-4 rounded-lg">
              <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:657:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="657" data-component-end-line="660" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
                <Icon data-component-id="src/app/booking-system/components/BookingInteractive.tsx:658:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="658" data-component-end-line="658" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationCircleIcon%22%7D" name="ExclamationCircleIcon" size={20} />
                <span data-component-id="src/app/booking-system/components/BookingInteractive.tsx:659:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="659" data-component-end-line="659" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%22%2C%22textContent%22%3A%22Errore%20di%20pagamento%3A%22%7D" className="font-semibold">Errore di pagamento:</span>
              </div>
              <p data-component-id="src/app/booking-system/components/BookingInteractive.tsx:661:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="661" data-component-end-line="661" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20mt-2%22%7D" className="text-sm mt-2">{paymentError}</p>
            </div>
          )}

          <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:665:10" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="665" data-component-end-line="703" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20pt-6%20border-t%20border-border%22%7D" className="flex items-center justify-between pt-6 border-t border-border">
            <button data-component-id="src/app/booking-system/components/BookingInteractive.tsx:666:12" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="666" data-component-end-line="673" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-6%20py-3%20border%20border-border%20rounded-lg%20hover%3Abg-muted%20transition-colors%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className="flex items-center space-x-2 px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon data-component-id="src/app/booking-system/components/BookingInteractive.tsx:671:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="671" data-component-end-line="671" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%7D" name="ChevronLeftIcon" size={20} />
              <span data-component-id="src/app/booking-system/components/BookingInteractive.tsx:672:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="672" data-component-end-line="672" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Indietro%22%7D">Indietro</span>
            </button>

            {currentStep < 4 ? (
              <button data-component-id="src/app/booking-system/components/BookingInteractive.tsx:676:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="676" data-component-end-line="683" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20bg-primary%20text-primary-foreground%20px-6%20py-3%20rounded-lg%20hover%3Abg-primary%2F90%20transition-colors%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceedToStep(currentStep)}
                className="flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span data-component-id="src/app/booking-system/components/BookingInteractive.tsx:681:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="681" data-component-end-line="681" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Continua%22%7D">Continua</span>
                <Icon data-component-id="src/app/booking-system/components/BookingInteractive.tsx:682:16" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="682" data-component-end-line="682" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%7D" name="ChevronRightIcon" size={20} />
              </button>
            ) : (
              <button data-component-id="src/app/booking-system/components/BookingInteractive.tsx:685:14" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="685" data-component-end-line="701" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20bg-success%20text-success-foreground%20px-8%20py-3%20rounded-lg%20hover%3Abg-success%2F90%20transition-colors%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
                onClick={handleBookingSubmit}
                disabled={!canProceedToStep(4) || isProcessing}
                className="flex items-center space-x-2 bg-success text-success-foreground px-8 py-3 rounded-lg hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:692:20" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="692" data-component-end-line="692" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-5%20w-5%20border-b-2%20border-success-foreground%22%7D" className="animate-spin rounded-full h-5 w-5 border-b-2 border-success-foreground"></div>
                    <span data-component-id="src/app/booking-system/components/BookingInteractive.tsx:693:20" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="693" data-component-end-line="693" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Elaborazione...%22%7D">Elaborazione...</span>
                  </>
                ) : (
                  <>
                    <Icon data-component-id="src/app/booking-system/components/BookingInteractive.tsx:697:20" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="697" data-component-end-line="697" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CreditCardIcon%22%7D" name="CreditCardIcon" size={20} />
                    <span data-component-id="src/app/booking-system/components/BookingInteractive.tsx:698:20" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="698" data-component-end-line="698" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Conferma%20Prenotazione%22%7D">Conferma Prenotazione</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div data-component-id="src/app/booking-system/components/BookingInteractive.tsx:706:8" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="706" data-component-end-line="717" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <BookingSummary data-component-id="src/app/booking-system/components/BookingInteractive.tsx:707:10" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="707" data-component-end-line="716" data-component-file="BookingInteractive.tsx" data-component-name="BookingSummary" data-component-content="%7B%22elementName%22%3A%22BookingSummary%22%7D"
            selectedDates={selectedDates}
            selectedRooms={selectedRooms}
            rooms={rooms}
            adults={adults}
            children={children}
            specialRequests={guestInfo.specialRequests}
            breakfastIncluded={guestInfo.breakfastIncluded}
            cleaningService={guestInfo.cleaningService}
          />
        </div>
      </div>
    </div>
  );
};

const BookingInteractive = () => {
  return (
    <Elements data-component-id="src/app/booking-system/components/BookingInteractive.tsx:725:4" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="725" data-component-end-line="727" data-component-file="BookingInteractive.tsx" data-component-name="Elements" data-component-content="%7B%22elementName%22%3A%22Elements%22%7D" stripe={stripePromise} options={{ locale: 'it' }}>
      <BookingForm data-component-id="src/app/booking-system/components/BookingInteractive.tsx:726:6" data-component-path="src/app/booking-system/components/BookingInteractive.tsx" data-component-line="726" data-component-end-line="726" data-component-file="BookingInteractive.tsx" data-component-name="BookingForm" data-component-content="%7B%22elementName%22%3A%22BookingForm%22%7D" />
    </Elements>
  );
};

export default BookingInteractive;
